import React, { useEffect, useCallback, memo } from 'react';
import loadable from '@loadable/component';
import { useHistory } from 'react-router-dom';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { useIntl } from '../../util/reactIntl';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = memo(props => {
  const { pageAssetsData, inProgress, error } = props;
  const intl = useIntl();
  const history = useHistory();

  // Memoize redirect logic with useCallback to prevent unnecessary re-renders
  const handleRedirect = useCallback(() => {
    try {
      const pathname = window.location.pathname || '/';
      const locale = (intl?.locale || '').toLowerCase();
      let selectedLanguage = null;
      try {
        selectedLanguage = window.localStorage ? window.localStorage.getItem('selectedLanguage') : null;
      } catch (_) {
        selectedLanguage = null;
      }

      const isArabicLocale = locale.startsWith('ar');
      const isArabicSelected = selectedLanguage === 'ar';

      if (pathname === '/' && (isArabicLocale || isArabicSelected)) {
        history.replace('/p/ar-home');
      }
    } catch (_) {
    }
  }, [intl?.locale, history]);

  // Avoid SSR/CSR mismatch: perform redirect client-side after mount
  useEffect(() => {
    handleRedirect();
  }, [handleRedirect]);

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
    />
  );
});

LandingPageComponent.displayName = 'LandingPageComponent';

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
